import { indentLess, indentMore } from "@codemirror/commands";
import { EditorView } from "@codemirror/view";
import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSize,
  ButtonSmallSubtleIcon,
  GenericButton,
  GenericDropdown,
  type GenericDropdownItem,
  Icon,
  Tooltip,
} from "@studio3t/generic-components";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { useId, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { buildExtensions, CONTEXT_MENU_HEIGHT } from "@/components/JsonViewer";
import {
  reindentScript,
  rhaiHighlightStyle,
  rhaiLanguage,
} from "@/pages/NewStudio/rhaiLanguage";
import {
  FormSection,
  Input,
  Label,
  Subsection,
  SubsectionText,
  SubsectionTitle,
} from "./styles";

const SEMANTIC = designVariables.ui.semantic;
const CARDS = designVariables.ui.cards;
const INPUT = designVariables.ui.input;

// Editor pane height per Figma Bridge_PipelineCode instance (14058-10677);
// longer scripts scroll inside CodeMirror.
const EDITOR_HEIGHT = "246px";

const IS_MAC =
  typeof navigator !== "undefined" &&
  (navigator.platform ?? "").toUpperCase().includes("MAC");
const KEY = (letter: string) => (IS_MAC ? `⌘${letter}` : `Ctrl+${letter}`);

// View-level clipboard actions (same recipe as the Studio Transform editor).
// Clipboard access can be denied — a failure is a no-op.
function copyDocSelection(view: EditorView) {
  const sel = view.state.selection.main;
  if (sel.empty) return null;
  navigator.clipboard
    .writeText(view.state.sliceDoc(sel.from, sel.to))
    .catch(() => {});
  return sel;
}

function cutDocSelection(view: EditorView): void {
  const sel = copyDocSelection(view);
  if (!sel) return;
  view.dispatch({ changes: { from: sel.from, to: sel.to } });
  view.focus();
}

async function pasteIntoView(view: EditorView): Promise<void> {
  const text = await navigator.clipboard.readText().catch(() => "");
  if (!text) return;
  view.dispatch(view.state.replaceSelection(text));
  view.focus();
}

export interface Script {
  id: number;
  filename: string;
  content: string;
}

// Filenames are compared and sent in normalised form so "transform" and
// "transform.rhai" count as the same file.
export function normalizeScriptFilename(filename: string): string {
  const trimmed = filename.trim();
  return trimmed.endsWith(".rhai") ? trimmed : `${trimmed}.rhai`;
}

// Filenames used by more than one script — the server would silently keep
// only the last one, so the form flags and rejects them instead.
export function duplicateScriptFilenames(scripts: Script[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const script of scripts) {
    if (!script.filename.trim()) continue;
    const name = normalizeScriptFilename(script.filename);
    if (seen.has(name)) duplicates.add(name);
    seen.add(name);
  }
  return duplicates;
}

// The Raw-configuration CodeMirror template speaking Rhai (same recipe as
// the Studio Transform editor), with the Transform tab's status-bar actions:
// format, indent either way, word wrap, clear.

// GenericButton derivatives drop type/aria props; inside the form their
// <button> defaults to type="submit", so a footer click would fire native
// validation ("Please fill out this field"). Both attributes go on via ref.
const actionRef = (label: string) => (el: HTMLButtonElement | null) => {
  el?.setAttribute("type", "button");
  el?.setAttribute("aria-label", label);
};

// The Transform tab's right-click menu minus the Studio-only template
// entries: clipboard, then the status-bar formatting actions.
function buildScriptContextItems({
  hasSelection,
  empty,
  wrap,
  withView,
  runCommand,
  toggleWrap,
}: {
  hasSelection: boolean;
  empty: boolean;
  wrap: boolean;
  withView: (action: (view: EditorView) => void) => void;
  runCommand: (command: typeof indentMore) => void;
  toggleWrap: () => void;
}): GenericDropdownItem[] {
  return [
    [
      {
        id: "cut",
        type: "text",
        value: "cut",
        label: "Cut",
        badgeText: KEY("X"),
        disabled: !hasSelection,
        closeOnSelect: true,
        onClick: () => withView(cutDocSelection),
      },
      {
        id: "copy",
        type: "text",
        value: "copy",
        label: "Copy",
        badgeText: KEY("C"),
        disabled: !hasSelection,
        closeOnSelect: true,
        onClick: () => withView(copyDocSelection),
      },
      {
        id: "paste",
        type: "text",
        value: "paste",
        label: "Paste",
        badgeText: KEY("V"),
        closeOnSelect: true,
        onClick: () => withView(pasteIntoView),
      },
      "SEPARATOR",
      {
        id: "format-script",
        type: "text",
        value: "format-script",
        label: "Format script",
        disabled: empty,
        closeOnSelect: true,
        onClick: () => runCommand(reindentScript),
      },
      {
        id: "indent-less",
        type: "text",
        value: "indent-less",
        label: "Decrease indent",
        disabled: empty,
        closeOnSelect: true,
        onClick: () => runCommand(indentLess),
      },
      {
        id: "indent-more",
        type: "text",
        value: "indent-more",
        label: "Increase indent",
        disabled: empty,
        closeOnSelect: true,
        onClick: () => runCommand(indentMore),
      },
      {
        id: "word-wrap",
        type: "text",
        value: "word-wrap",
        label: wrap ? "Disable word wrap" : "Enable word wrap",
        disabled: empty,
        closeOnSelect: true,
        onClick: () => toggleWrap(),
      },
    ],
  ];
}

// The editor footer: the shared formatting actions and Clear, disabled on an
// empty script.
function ScriptStatusBar({
  empty,
  wrap,
  runCommand,
  toggleWrap,
  onClear,
}: {
  empty: boolean;
  wrap: boolean;
  runCommand: (command: typeof indentMore) => void;
  toggleWrap: () => void;
  onClear: () => void;
}) {
  return (
    <StatusBar>
      <Tooltip text="Format script" side="top">
        <ButtonSmallSubtleIcon
          ref={actionRef("Format script")}
          icon={Icon.FormatQuery}
          onClick={() => runCommand(reindentScript)}
          disabled={empty}
        />
      </Tooltip>
      <Tooltip text="Decrease indent" side="top">
        <ButtonSmallSubtleIcon
          ref={actionRef("Decrease indent")}
          icon={Icon.IndentLeft}
          onClick={() => runCommand(indentLess)}
          disabled={empty}
        />
      </Tooltip>
      <Tooltip text="Increase indent" side="top">
        <ButtonSmallSubtleIcon
          ref={actionRef("Increase indent")}
          icon={Icon.IndentRight}
          onClick={() => runCommand(indentMore)}
          disabled={empty}
        />
      </Tooltip>
      <Tooltip
        text={wrap ? "Disable word wrap" : "Enable word wrap"}
        side="top"
      >
        <ButtonSmallSubtleIcon
          ref={actionRef("Toggle word wrap")}
          icon={Icon.WordWrap}
          onClick={toggleWrap}
          disabled={empty}
        />
      </Tooltip>
      <StatusDivider />
      <Tooltip text="Clear" side="top">
        <ButtonSmallSubtleIcon
          ref={actionRef("Clear script")}
          icon={Icon.Clean}
          onClick={onClear}
          disabled={empty}
        />
      </Tooltip>
    </StatusBar>
  );
}

function ScriptCodeEditor({
  content,
  onContentChange,
}: {
  content: string;
  onContentChange: (value: string) => void;
}) {
  const theme = useTheme();
  const cmRef = useRef<ReactCodeMirrorRef>(null);
  const editorAreaRef = useRef<HTMLDivElement>(null);
  const [wrap, setWrap] = useState(true);
  const [hasSelection, setHasSelection] = useState(false);
  const empty = content === "";

  const extensions = useMemo(
    () => [
      ...buildExtensions(theme, {
        lineWrapping: wrap,
        language: rhaiLanguage,
        highlight: rhaiHighlightStyle,
      }),
      EditorView.updateListener.of((u) => {
        if (!u.selectionSet && !u.docChanged) return;
        setHasSelection(!u.state.selection.main.empty);
      }),
    ],
    [theme, wrap],
  );

  const runCommand = (command: typeof indentMore) => {
    const view = cmRef.current?.view;
    if (!view) return;
    command(view);
    view.focus();
  };

  const withView = (action: (view: EditorView) => void) => {
    const view = cmRef.current?.view;
    if (view) action(view);
  };

  const contextItems = buildScriptContextItems({
    hasSelection,
    empty,
    wrap,
    withView,
    runCommand,
    toggleWrap: () => setWrap((prev) => !prev),
  });

  return (
    <>
      <EditorArea ref={editorAreaRef}>
        <CodeMirror
          ref={cmRef}
          value={content}
          onChange={onContentChange}
          extensions={extensions}
          height={EDITOR_HEIGHT}
          placeholder="fn transform(data, attributes) { ... }"
          basicSetup={{
            lineNumbers: true,
            foldGutter: false,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            highlightActiveLine: false,
            highlightActiveLineGutter: false,
          }}
        />
        <GenericDropdown
          asContextMenu={editorAreaRef}
          dropdownItems={contextItems}
          width={{ strategy: "fixed", size: 220 }}
          height={CONTEXT_MENU_HEIGHT}
        />
      </EditorArea>
      <ScriptStatusBar
        empty={empty}
        wrap={wrap}
        runCommand={runCommand}
        toggleWrap={() => setWrap((prev) => !prev)}
        onClear={() => onContentChange("")}
      />
    </>
  );
}

interface UdfFieldsProps {
  scripts: Script[];
  onAddScript: () => void;
  onRemoveScript: (id: number) => void;
  onFilenameChange: (id: number, filename: string) => void;
  onContentChange: (id: number, content: string) => void;
}

export function UdfFields({
  scripts,
  onAddScript,
  onRemoveScript,
  onFilenameChange,
  onContentChange,
}: UdfFieldsProps) {
  const theme = useTheme();
  const filenameId = useId();
  const duplicateFilenames = duplicateScriptFilenames(scripts);
  // theme.errorIcon carries only default/hover colours and IconSVG paints
  // nothing in a state it has no colour for, so the pressed and disabled
  // ones are supplied (the ServiceDetail bin recipe).
  const binColors = {
    ...theme.errorIcon,
    active: theme.errorIcon.default,
    disabled: theme.buttonSubtleDisabledText,
  };

  return (
    <FormSection>
      <Subsection>
        <SubsectionTitle>Scripts</SubsectionTitle>
        <SubsectionText>
          Add Rhai transformation scripts. Each script must have a
          transform(data, attributes) function.
        </SubsectionText>

        {scripts.map((script) => {
          const hasFilenameError = !script.filename.trim();
          const hasContentError = !script.content.trim();
          const isDuplicate =
            !hasFilenameError &&
            duplicateFilenames.has(normalizeScriptFilename(script.filename));
          return (
            <ScriptCard key={script.id}>
              <ScriptCardHeader>
                <Label htmlFor={`${filenameId}-${script.id}`}>
                  Script filename
                </Label>
                <Input
                  id={`${filenameId}-${script.id}`}
                  type="text"
                  value={script.filename}
                  onChange={(e) => onFilenameChange(script.id, e.target.value)}
                  placeholder="script_name.rhai"
                />
                {hasFilenameError && (
                  <ScriptError>Filename required</ScriptError>
                )}
                {isDuplicate && <ScriptError>Duplicate filename</ScriptError>}
                <ButtonSmallSubtleIcon
                  // GenericButton derivatives drop type/aria props; both
                  // are set on the real <button> via ref (type defaults to
                  // "submit" inside the form).
                  ref={(el) => {
                    el?.setAttribute("type", "button");
                    el?.setAttribute(
                      "aria-label",
                      `Remove script ${script.filename || script.id + 1}`,
                    );
                  }}
                  icon={Icon.Bin}
                  colors={binColors}
                  onClick={() => onRemoveScript(script.id)}
                  // The bin stays in the header on a lone script, disabled
                  // (Figma 14058-93700): a service needs at least one.
                  disabled={scripts.length === 1}
                />
              </ScriptCardHeader>
              <ScriptCodeEditor
                content={script.content}
                onContentChange={(value) => onContentChange(script.id, value)}
              />
              {hasContentError && (
                <ContentError>Script content cannot be empty</ContentError>
              )}
            </ScriptCard>
          );
        })}

        <div>
          <GenericButton
            // GenericButton's `type` prop is the variant; the HTML type
            // attribute is set via ref.
            ref={(el) => el?.setAttribute("type", "button")}
            type="secondary"
            size={ButtonSize.Small}
            icon={Icon.Plus}
            iconPosition="leading"
            text="Add another script"
            onClick={onAddScript}
          />
        </div>
      </Subsection>
    </FormSection>
  );
}

// Hosts the CodeMirror pane and anchors its right-click context menu.
const EditorArea = styled.div`
  position: relative;
`;

// Script card per Figma 14058-10677: filename header row, the code editor,
// and the editor-actions status bar, inside one rounded border.
const ScriptCard = styled.div`
  width: 100%;
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-radius: ${CARDS.corner}px;
  overflow: hidden;
`;

const ScriptCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${INPUT.gaplabel}px;
  padding: 10px ${SEMANTIC.spacingl}px;
  border-bottom: 1px solid ${(props) => props.theme.cards1Border};

  > ${Input} {
    flex: 1;
    min-width: 0;
  }
`;

const ScriptError = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: #dc2626;
  white-space: nowrap;
`;

const ContentError = styled.div`
  padding: 6px 12px;
  font-size: 11px;
  color: #dc2626;
  background: #fee2e2;
`;

// Same StatusBar metrics as the Studio Transform editor (Figma StatusBar,
// UI-Modules 1660-55289).
const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px;
  background: ${(props) => props.theme.editorStatusBackground};
  border-top: 1px solid ${(props) => props.theme.editorStatusBorder};
`;

const StatusDivider = styled.span`
  width: 8px;
  height: 20px;
  flex-shrink: 0;
  background-image: linear-gradient(
    ${(props) => props.theme.editorStatusBorder},
    ${(props) => props.theme.editorStatusBorder}
  );
  background-size: 1px 100%;
  background-position: center;
  background-repeat: no-repeat;
`;
